import React, { useState } from 'react';
import { useTheme, } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import axios from 'axios';
import {
  Box,
  Container,
  Dialog,
  Typography,
  Button,
  Grid,
  TextField
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  CheckCircleOutline,
  ErrorOutline
} from '@mui/icons-material';

function Contact() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service: "",
  });
  const [openPopup, setOpenPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        "https://saini-collection-backend.onrender.com/api/v1/sc/create-contact",
        {
          senderName: formData.name,
          senderEmail: formData.email,
          phoneNo: formData.phone,
          message: formData.message,
          service: formData.service
        }
      );
      setPopupMessage("Your message has been sent successfully!");
      setIsSuccess(true);
      setOpenPopup(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        service: "",
      });
    } catch (error) {
      setPopupMessage("Oops! Something went wrong. Please try again.");
      setIsSuccess(false);
      setOpenPopup(true);
    }
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
  };

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 4, md: 10 },
        background: "#ffffff",
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        <Dialog
          open={openPopup}
          onClose={handleClosePopup}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              p: isMobile ? 3 : 4,
              maxWidth: isMobile ? '90%' : '400px',
              textAlign: 'center',
              border: isSuccess ? `2px solid ${theme.palette.secondary.main}` : `2px solid ${theme.palette.error.main}`
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {isSuccess ? (
              <CheckCircleOutline
                sx={{
                  fontSize: isMobile ? 56 : 72,
                  color: theme.palette.secondary.main,
                  mb: 2
                }}
              />
            ) : (
              <ErrorOutline
                sx={{
                  fontSize: isMobile ? 56 : 72,
                  color: theme.palette.error.main,
                  mb: 2
                }}
              />
            )}

            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: 2,
                fontWeight: 700,
                color: isSuccess ? theme.palette.secondary.main : theme.palette.error.main
              }}
            >
              {isSuccess ? "Success!" : "Error!"}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: theme.palette.secondary.main,
                fontSize: isMobile ? '1rem' : '1.1rem'
              }}
              component="div"
            >
              {popupMessage}
            </Typography>

            <Button
              variant="contained"
              onClick={handleClosePopup}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: isMobile ? '0.9rem' : '1rem',
                backgroundColor: isSuccess ? theme.palette.secondary.main : theme.palette.error.main,
                '&:hover': {
                  backgroundColor: isSuccess ? theme.palette.secondary.dark : theme.palette.error.dark
                }
              }}
            >
              Thanks for your Interest
            </Button>
          </Box>
        </Dialog>

        <Typography
          variant={isMobile ? "h3" : "h2"}
          gutterBottom
          sx={{
            color: theme.palette.primary.main,
            fontSize: { xs: "1.5rem", sm: "2.4rem", md: "3rem" },
            fontWeight: 700,
            textAlign: "center",
            mb: { xs: 4, md: 6 },
            position: "relative",
            "&:after": {
              content: '""',
              display: "block",
              width: 150,
              height: 5,
              background: theme.palette.secondary.main,
              margin: "0px auto 0",
              borderRadius: 10,

            }
          }}
        >
          Let’s
          Talk
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 4,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              flex: 1,
              p: { xs: 2, md: 4 },
              borderRadius: 4,
              background: "rgba(234, 241, 255, 0.5)",
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.primary.light}`,
              minWidth: 0,
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: 3,
                color: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: theme.palette.secondary.main,
                }}
              />
              Contact Information
            </Typography>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}
            >
              {[
                {
                  icon: (
                    <Email
                      sx={{ color: theme.palette.secondary.main, fontSize: isMobile ? 24 : 28 }}
                    />
                  ),
                  title: "Email",
                  content: "info@sainicollection.com",
                },
                {
                  icon: (
                    <Phone
                      sx={{ color: theme.palette.secondary.main, fontSize: isMobile ? 24 : 28 }}
                    />
                  ),
                  title: "Phone",
                  content: "+91-8700274294",
                },
                {
                  icon: (
                    <LocationOn
                      sx={{ color: theme.palette.secondary.main, fontSize: isMobile ? 24 : 28 }}
                    />
                  ),
                  title: "Location",
                  content:
                    "Saini Collection, Mahavir Pura, Gurgaon(Haryana), India",
                },
              ].map((item, index) => (
                <Box key={index} sx={{ display: "flex", gap: 3 }}>
                  <Box
                    sx={{
                      width: isMobile ? 48 : 56,
                      height: isMobile ? 48 : 56,
                      borderRadius: "50%",
                      background: "rgba(74, 123, 255, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        mb: 0.5,
                        fontSize: isMobile ? '0.95rem' : '1rem'
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: isMobile ? '0.9rem' : '1rem'
                      }}
                      component="div"
                    >
                      {item.content}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                p: { xs: 3, md: 7 },
                borderRadius: 3,
                background: "rgba(26, 58, 111, 0.03)",
                border: `1px solid ${theme.palette.primary.light}`,
              }}
            >
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  fontSize: { xs: '1rem', md: '1.5rem' },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: theme.palette.secondary.main,
                  }}
                />
                Business Hours
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {[
                  "Monday - Friday: 9:00 AM - 6:00 PM",
                ].map((item, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      fontSize: { xs: '0.81rem', md: '1rem' },
                    }}
                    component="div"
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: theme.palette.secondary.main,
                      }}
                    />
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              p: { xs: 2, md: 4 },
              borderRadius: 4,
              background: "#ffffff",
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
              border: `1px solid ${theme.palette.primary.light}`,
              minWidth: 0,
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: 3,
                color: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: theme.palette.secondary.main,
                }}
              />
              Send us a message
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Box sx={{ width: "100%" }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      variant="outlined"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      variant="outlined"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      variant="outlined"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Service Interested In"
                      variant="outlined"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      SelectProps={{ native: true }}
                      size={isMobile ? "small" : "medium"}
                    >
                      <option value=""></option>
                      <option value="web-design">Website Design</option>
                      <option value="frontend-backend">
                        Frontend & Backend Development
                      </option>
                      <option value="custom-tools">Custom Tools</option>
                      <option value="maintenance">Website Maintenance</option>
                      <option value="ecommerce">E-commerce Solutions</option>
                    </TextField>
                  </Grid>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Your Message"
                      variant="outlined"
                      name="message"
                      multiline
                      rows={isMobile ? 3 : 4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                </Box>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    size={isMobile ? "medium" : "large"}
                    fullWidth
                    sx={{
                      py: isMobile ? 1 : 1.5,
                      fontWeight: 700,
                      mt: 1,
                    }}
                  >
                    Connect With Us
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Container>
    </Box >
  );
}

export default Contact;